import {
  ArrowRight,
  BookOpen,
  Check,
  CircleCheck,
  Clipboard,
  Clock3,
  Command,
  ExternalLink,
  Gauge,
  KeyRound,
  Menu,
  Search,
  ShieldCheck,
  Terminal,
  Webhook,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  apiErrors,
  billingModes,
  brCreateFields,
  brDefaultRequestSample,
  brEmulatorsRequestSample,
  brReadyResponseSample,
  brRuleFields,
  brStatusResponseSample,
  customFunctions,
  itemCatalog,
  playerErrors,
  playerV1Fields,
  playerV1Sample,
  playerV2Fields,
  playerV2Sample,
  resultResponseSample,
  roomFields,
  slotFields,
  startResponseSample,
  statusResponseSample,
  statusRootFields,
  webhookVerificationSample,
  type ReferenceField,
} from "./docsData";

type Method = "GET" | "POST";
type ProductId = "rxsalas" | "rxsearch";
type CodeLanguage = "curl" | "node";

interface RoomMode {
  id: string;
  label: string;
  pref?: string;
  description: string;
  useCase: string;
  rounds: string;
  economy: string;
  mechanic: string;
  shop: string;
  variants?: Array<{
    label: string;
    pref: string;
    economy: string;
    note: string;
  }>;
  custom?: boolean;
}

interface NavigationItem {
  id: string;
  label: string;
  detail: string;
  method?: Method;
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

interface Product {
  id: ProductId;
  name: string;
  summary: string;
  baseUrl: string;
  home: string;
  footerNote: string;
  navigation: NavigationGroup[];
  toc: Array<{ id: string; label: string }>;
}

interface SearchResult extends NavigationItem {
  group: string;
  modeId?: string;
}

const normalizeForSearch = (value: string) => value
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLocaleLowerCase("pt-BR");

// Cada produto tem navegação, conteúdo e índice próprios. Os ids das seções são
// únicos entre produtos: um link antigo como #consulta-v1 abre direto no RX.SEARCH.
const products: Product[] = [
  {
    id: "rxsalas",
    name: "RX.SALAS",
    summary: "Criação e controle de salas",
    baseUrl: "api.rushxclub.com",
    home: "inicio",
    footerNote: "Referência operacional da API de salas",
    navigation: [
      {
        label: "Começar",
        items: [
          { id: "inicio", label: "Visão geral", detail: "Base, fluxo e primeiros passos" },
          { id: "autenticacao", label: "Autenticação", detail: "Chave da API e segurança" },
        ],
      },
      {
        label: "Criar",
        items: [
          { id: "criar-sala", label: "Criar sala", detail: "Solicite uma nova sala", method: "POST" },
          { id: "criar-sala-br", label: "Criar sala BR", detail: "Battle Royale, circuito, rules e emuladores", method: "POST" },
          { id: "acompanhar", label: "Acompanhar criação", detail: "Consulte uma solicitação", method: "GET" },
        ],
      },
      {
        label: "Operar",
        items: [
          { id: "status-sala", label: "Status da sala", detail: "Lobby, times, perfis e cache", method: "GET" },
          { id: "operacoes-sala", label: "Controlar sala", detail: "Start, kick, edit, chat e release" },
          { id: "resultados", label: "Resultado", detail: "Placar, tracking, jogadores e polling", method: "GET" },
          { id: "join-events", label: "Entradas", detail: "Eventos incrementais do lobby", method: "GET" },
        ],
      },
      {
        label: "Integração",
        items: [
          { id: "webhooks", label: "Webhooks", detail: "Assinatura, eventos e retentativas", method: "POST" },
          { id: "cobranca", label: "Cobrança e saldo", detail: "Carteira, planos e billing_mode" },
        ],
      },
      {
        label: "Referência",
        items: [
          { id: "custom-ref", label: "Referência do custom", detail: "Funções, atributos e catálogo" },
          { id: "erros", label: "Erros", detail: "Códigos e recuperação" },
          { id: "limites", label: "Limites e cotas", detail: "Headers e Retry-After" },
          { id: "boas-praticas", label: "Boas práticas", detail: "Checklist para produção" },
        ],
      },
    ],
    toc: [
      { id: "inicio", label: "Visão geral" },
      { id: "autenticacao", label: "Autenticação" },
      { id: "criar-sala", label: "Criar sala" },
      { id: "criar-sala-br", label: "Criar sala BR" },
      { id: "status-sala", label: "Status" },
      { id: "operacoes-sala", label: "Operações" },
      { id: "resultados", label: "Resultado" },
      { id: "webhooks", label: "Webhooks" },
      { id: "custom-ref", label: "Custom" },
      { id: "erros", label: "Erros" },
    ],
  },
  {
    id: "rxsearch",
    name: "RX.SEARCH",
    summary: "Consulta de jogador por UID",
    baseUrl: "search.rushxclub.com",
    home: "consulta-jogador",
    footerNote: "Referência da API de consulta de jogador",
    navigation: [
      {
        label: "Começar",
        items: [
          { id: "consulta-jogador", label: "Visão geral", detail: "search.rushxclub.com, rotas e versões" },
          { id: "consulta-autenticacao", label: "Autenticação", detail: "Chave rxs_ e headers aceitos" },
        ],
      },
      {
        label: "Consultar",
        items: [
          { id: "consulta-v1", label: "Jogador v1", detail: "Identificação enxuta e blacklist", method: "GET" },
          { id: "consulta-v2", label: "Jogador v2", detail: "Perfil completo com passes", method: "GET" },
        ],
      },
      {
        label: "Referência",
        items: [
          { id: "consulta-erros", label: "Erros e limites", detail: "Códigos, cota e cache da consulta" },
        ],
      },
    ],
    toc: [
      { id: "consulta-jogador", label: "Visão geral" },
      { id: "consulta-autenticacao", label: "Autenticação" },
      { id: "consulta-v1", label: "Jogador v1" },
      { id: "consulta-v2", label: "Jogador v2" },
      { id: "consulta-erros", label: "Erros e limites" },
    ],
  },
];

const productById = new Map(products.map((product) => [product.id, product]));
const productBySection = new Map<string, ProductId>(
  products.flatMap((product) => product.navigation.flatMap((group) => group.items.map((item) => [item.id, product.id] as const))),
);

// undefined para âncoras que não são seção de produto (#conteudo, por exemplo): não troca de produto.
function productOfSection(id: string): ProductId | undefined {
  if (id.startsWith("mode-")) return "rxsalas";
  return productBySection.get(id);
}

const responseSample = `{
  "requestId": "req_01J7M8FK2X",
  "status": "completed",
  "created": true,
  "request": {
    "room": {
      "session_id": "ses_3fd74a",
      "room_id": "7281943",
      "password": "8137"
    }
  }
}`;

const customResponseSample = `{
  "requestId": "req_01J7M8FK2X",
  "status": "completed",
  "created": true,
  "request": {
    "room": {
      "session_id": "ses_3fd74a",
      "room_id": "7281943",
      "password": "1234"
    }
  },
  "config_applied": {
    "team_size": "4x4",
    "map": "default",
    "rounds": 7,
    "attributes": { "hp": 1, "ep": 1, "speed": 1, "jump": 1 },
    "shop": [
      { "item": "DESERT_EAGLE", "price": 0 },
      { "item": "GLOO_WALL", "price": 0 }
    ],
    "gold_per_round": [0, 9950, 9950, 9950, 9950, 9950, 9950]
  }
}`;

const roomModes: RoomMode[] = [
  {
    id: "sem-carregamento",
    label: "Sem carregamento",
    pref: "cs_8_sem_carregamento",
    description: "O competitivo padrão: os itens do perfil ficam desligados e todos jogam a partir da mesma loja.",
    useCase: "X1, X4 e campeonatos com equipamento nivelado",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Carregamento desligado",
    shop: "Armas competitivas e equipamento padrão",
    variants: [
      {
        label: "M2",
        pref: "cs_8_sem_carregamento_m2",
        economy: "1.500 no round 1; 9.950 nos demais",
        note: "Mantém as regras e a loja do modo normal.",
      },
      {
        label: "M3",
        pref: "cs_8_sem_carregamento_m3",
        economy: "Sem ouro no round 1; 9.950 nos demais",
        note: "O primeiro round usa um kit gratuito fechado.",
      },
    ],
  },
  {
    id: "com-carregamento",
    label: "Com carregamento",
    pref: "cs_8_com_carregamento",
    description: "Mantém o carregamento do perfil habilitado, preservando as escolhas de itens feitas por cada jogador.",
    useCase: "Partidas que permitem recursos individuais",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Carregamento ligado",
    shop: "Mesma seleção competitiva do modo padrão",
  },
  {
    id: "gelo-infinito",
    label: "Gelo infinito",
    pref: "cs_8_gelo_infinito",
    description: "Replica o modo padrão, mas libera paredes de gelo sem limite durante a partida.",
    useCase: "Treinos de movimentação, cobertura e troca curta",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Gelo infinito; carregamento desligado",
    shop: "Armas competitivas e equipamento padrão",
    variants: [
      {
        label: "M2",
        pref: "cs_8_gelo_infinito_m2",
        economy: "1.500 no round 1; 9.950 nos demais",
        note: "Mantém o gelo infinito e a loja do modo normal.",
      },
      {
        label: "M3",
        pref: "cs_8_gelo_infinito_m3",
        economy: "Sem ouro no round 1; 9.950 nos demais",
        note: "O primeiro round usa o kit gratuito e mantém gelo infinito.",
      },
    ],
  },
  {
    id: "gelo-capa",
    label: "Gelo + capa",
    pref: "cs_8_gelo_capa",
    description: "Combina paredes de gelo ilimitadas com dano válido apenas em acertos na cabeça.",
    useCase: "Treino avançado de capa com cobertura intensa",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Gelo infinito + somente headshot",
    shop: "Armas competitivas e equipamento padrão",
  },
  {
    id: "full-capa",
    label: "Full capa",
    pref: "cs_8_full_capa",
    description: "Somente acertos na cabeça causam dano, sem alterar o conjunto competitivo de armas.",
    useCase: "Treino de precisão e torneios de capa",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Somente headshot",
    shop: "Armas competitivas e equipamento padrão",
  },
  {
    id: "ump-xm8",
    label: "UMP & XM8",
    pref: "cs_8_ump_xm8",
    description: "Reduz a loja às armas centrais UMP e XM8, com Desert Eagle e o equipamento essencial.",
    useCase: "Confronto focado em SMG e rifle",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Carregamento desligado",
    shop: "UMP, XM8, Desert Eagle e equipamento",
  },
  {
    id: "soco",
    label: "Soco",
    pref: "cs_8_soco",
    description: "Remove os itens da loja para criar um confronto corpo a corpo puro.",
    useCase: "Desempates, eventos rápidos e desafios",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Carregamento desligado; sem armas na loja",
    shop: "Vazia",
  },
  {
    id: "tatico",
    label: "Tático",
    pref: "cs_8_tatico",
    description: "Abre uma loja ampla, mas remove armas e itens que quebram o ritmo competitivo do modo.",
    useCase: "Campeonatos com maior variedade estratégica",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "Munição limitada; carregamento desligado",
    shop: "Seleção tática ampla, sem granadas",
  },
  {
    id: "speed",
    label: "Speed",
    pref: "cs_8_speed",
    description: "Versão curta do modo padrão. O nome não altera a velocidade dos jogadores; muda somente a duração.",
    useCase: "Chaves rápidas, classificatórias e testes de fluxo",
    rounds: "7",
    economy: "9.950 de ouro por round",
    mechanic: "Mesmas regras do modo padrão",
    shop: "Armas competitivas e equipamento padrão",
  },
  {
    id: "full-granada",
    label: "Granada",
    pref: "cs_8_full_granada",
    description: "Partida de arremessáveis com granada e gelo ilimitados, vida maior e 125% de velocidade.",
    useCase: "Evento especial e treino de granada",
    rounds: "13",
    economy: "9.950 de ouro por round",
    mechanic: "500 HP + velocidade 125% + arremessáveis infinitos",
    shop: "Granada, gelo, colete nível 3 e capacete nível 2",
  },
  {
    id: "custom",
    label: "Sala personalizada",
    description: "Cria uma combinação própria de funções, atributos, loja, preços, ouro por round e duração.",
    useCase: "Regras exclusivas que não cabem em um preset",
    rounds: "7 a 13",
    economy: "Definida por round no request",
    mechanic: "13 funções + 4 atributos configuráveis",
    shop: "1 a 70 itens, com preço individual",
    custom: true,
  },
];

const customRoomBody = {
  room_name: "X1 DESERT",
  password: "1234",
  goConsensus: true,
  config: {
    team_size: "4x4",
    map: "default",
    rounds: 7,
    functions: {
      loadout: false,
      unlimited_gloo: true,
      unlimited_ammo: false,
      head_shot_only: false,
      no_airdrop: true,
      no_vehicle: true,
      hide_kill_info: false,
      no_fall_damage: false,
      no_skill: false,
      no_power_gun: false,
      acc_total_stats: false,
      no_uav: false,
      no_bomb: false,
    },
    attributes: { hp: 1, ep: 1, speed: 1, jump: 1 },
    shop: [
      { item: "DESERT_EAGLE", price: 0 },
      { item: "GLOO_WALL", price: 0 },
    ],
    gold_per_round: [0, 9950, 9950, 9950, 9950, 9950, 9950],
  },
};

function buildModeCodeSamples(mode: RoomMode): Record<CodeLanguage, string> {
  const endpoint = mode.custom ? "/rooms/custom" : "/rooms";
  const body = mode.custom
    ? customRoomBody
    : { pref: mode.pref, room_name: "FINAL RX", password: "8137" };
  const json = JSON.stringify(body, null, 2);

  return {
    curl: `curl --request POST \\
  --url https://api.rushxclub.com${endpoint} \\
  --header 'Content-Type: application/json' \\
  --header 'X-API-Key: rxc_live_sua_chave' \\
  --header 'Idempotency-Key: 8c69b6c1-...' \\
  --data '${json}'`,
    node: `const response = await fetch("https://api.rushxclub.com${endpoint}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": process.env.RXSALAS_API_KEY,
    "Idempotency-Key": crypto.randomUUID(),
  },
  body: JSON.stringify(${json}),
});

const data = await response.json();`,
  };
}

function MethodBadge({ method }: { method: Method }) {
  return <span className={`method method--${method.toLowerCase()}`}>{method}</span>;
}

function CopyButton({ value, label = "Copiar" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_600);
  };

  return (
    <button className="copy-button" type="button" onClick={copy} aria-label={`${label} código`}>
      {copied ? <Check size={15} aria-hidden="true" /> : <Clipboard size={15} aria-hidden="true" />}
      <span>{copied ? "Copiado" : label}</span>
    </button>
  );
}

function EndpointTitle({ method, path, children }: { method: Method; path: string; children: string }) {
  return (
    <div className="endpoint-title">
      <div className="endpoint-route">
        <MethodBadge method={method} />
        <code>{path}</code>
      </div>
      <h2>{children}</h2>
    </div>
  );
}

function StaticCodeBlock({ value, label = "application/json" }: { value: string; label?: string }) {
  return (
    <div className="code-panel">
      <div className="code-toolbar"><span>{label}</span><CopyButton value={value} /></div>
      <pre><code>{value}</code></pre>
    </div>
  );
}

function FieldReference({ fields }: { fields: ReferenceField[] }) {
  return (
    <div className="reference-fields">
      {fields.map((field) => (
        <div className="reference-field" key={field.name}>
          <code>{field.name}</code>
          <span>{field.type}</span>
          <p>{field.description}</p>
        </div>
      ))}
    </div>
  );
}

function ProductSwitcher({ active, onSelect, className }: { active: ProductId; onSelect: (id: ProductId) => void; className: string }) {
  return (
    <div className={`product-switcher ${className}`} role="group" aria-label="Produto da documentação">
      {products.map((product) => (
        <button
          type="button"
          key={product.id}
          aria-pressed={active === product.id}
          title={product.summary}
          onClick={() => onSelect(product.id)}
        >
          <span>RX</span>{product.name.slice(2)}
        </button>
      ))}
    </div>
  );
}

function ProductTabs({ active, onSelect }: { active: ProductId; onSelect: (id: ProductId) => void }) {
  return (
    <nav className="product-tabs" aria-label="Produtos da documentação">
      <div className="product-tabs-list">
        {products.map((product) => (
          <button
            type="button"
            key={product.id}
            className="product-tab"
            aria-current={active === product.id ? "page" : undefined}
            onClick={() => onSelect(product.id)}
          >
            <strong>{product.name}</strong>
            <span>{product.summary}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

export function App() {
  const [productId, setProductId] = useState<ProductId>(() => productOfSection(window.location.hash.slice(1)) ?? "rxsalas");
  const product = productById.get(productId) ?? products[0];
  const [activeId, setActiveId] = useState(() => {
    const hash = window.location.hash.slice(1);
    return productOfSection(hash) && !hash.startsWith("mode-") ? hash : product.home;
  });
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [language, setLanguage] = useState<CodeLanguage>("curl");
  const [selectedModeId, setSelectedModeId] = useState(roomModes[0].id);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedMode = useMemo(
    () => roomModes.find((mode) => mode.id === selectedModeId) ?? roomModes[0],
    [selectedModeId],
  );
  const selectedCodeSamples = useMemo(() => buildModeCodeSamples(selectedMode), [selectedMode]);

  const searchResults = useMemo<SearchResult[]>(() => {
    const normalized = normalizeForSearch(query.trim());
    if (!normalized) return [];
    const topics: SearchResult[] = [
      ...products.flatMap((item) => item.navigation.flatMap((group) => group.items.map((entry) => ({ ...entry, group: group.label })))),
      ...roomModes.map((mode) => ({
        id: "criar-sala",
        label: mode.label,
        detail: `${mode.pref ?? "/rooms/custom"} ${mode.description} ${mode.mechanic} ${mode.economy}`,
        group: "Modos de sala",
        method: "POST" as const,
        modeId: mode.id,
      })),
      ...[...brCreateFields, ...brRuleFields].map((field) => ({
        id: "criar-sala-br",
        label: field.name,
        detail: `${field.type} ${field.description}`,
        group: "Battle Royale",
        method: "POST" as const,
      })),
      ...statusRootFields.map((field) => ({
        id: "status-sala",
        label: field.name,
        detail: `${field.type} ${field.description}`,
        group: "Campos do status",
        method: "GET" as const,
      })),
      ...roomFields.map((field) => ({
        id: "status-sala",
        label: `room.${field.name}`,
        detail: `${field.type} ${field.description}`,
        group: "Objeto room",
        method: "GET" as const,
      })),
      ...slotFields.map((field) => ({
        id: "status-sala",
        label: `slot.${field.name}`,
        detail: `${field.type} ${field.description}`,
        group: "Jogadores e espectadores",
        method: "GET" as const,
      })),
      ...billingModes.map((mode) => ({
        id: "cobranca",
        label: mode.name,
        detail: `${mode.behavior} ${mode.gate}`,
        group: "Cobrança",
      })),
      ...customFunctions.map((fn) => ({
        id: "custom-ref",
        label: fn.name,
        detail: `${fn.description} ${fn.status}`,
        group: "Funções custom",
      })),
      ...itemCatalog.flatMap((category) => category.items.map((item) => ({
        id: "custom-ref",
        label: item,
        detail: category.group,
        group: "Catálogo de itens",
      }))),
      ...apiErrors.map((error) => ({
        id: "erros",
        label: error.code,
        detail: `HTTP ${error.status} ${error.action}`,
        group: "Erros da API",
      })),
      ...playerV1Fields.map((field) => ({
        id: "consulta-v1",
        label: field.name,
        detail: `${field.type} ${field.description}`,
        group: "Consulta v1",
        method: "GET" as const,
      })),
      ...playerV2Fields.map((field) => ({
        id: "consulta-v2",
        label: field.name,
        detail: `${field.type} ${field.description}`,
        group: "Consulta v2",
        method: "GET" as const,
      })),
      ...playerErrors.map((error) => ({
        id: "consulta-erros",
        label: error.code,
        detail: `HTTP ${error.status} ${error.action}`,
        group: "Erros da consulta",
      })),
    ];
    const inActiveProduct = (item: SearchResult) => (productOfSection(item.id) === productId ? 1 : 0);
    return topics
      .filter((item) => normalizeForSearch(`${item.label} ${item.detail} ${item.id}`).includes(normalized))
      .sort((a, b) => inActiveProduct(b) - inActiveProduct(a))
      .slice(0, 8);
  }, [query, productId]);

  useEffect(() => {
    document.title = `Documentação - ${product.name}`;
  }, [product.name]);

  // Seção ativa: a última cujo topo já passou de 30% da tela; no fim da página, a última.
  // Por proporção visível (IntersectionObserver) uma seção curta vencia a longa logo abaixo.
  useEffect(() => {
    const sections = Array.from(document.querySelectorAll<HTMLElement>("main section[id]"));
    let frame = 0;
    const update = () => {
      frame = 0;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      const passed = sections.filter((section) => section.getBoundingClientRect().top <= window.innerHeight * 0.3);
      const current = atBottom ? sections[sections.length - 1] : passed[passed.length - 1];
      if (current) setActiveId(current.id);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [productId]);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    if (hash.startsWith("mode-")) {
      const modeId = hash.slice(5);
      if (roomModes.some((mode) => mode.id === modeId)) {
        setSelectedModeId(modeId);
        window.requestAnimationFrame(() => document.getElementById("criar-sala")?.scrollIntoView());
      }
      return;
    }
    window.requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView());
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.slice(1);
      const target = productOfSection(hash);
      if (!target || hash.startsWith("mode-")) return;
      flushSync(() => setProductId(target));
      document.getElementById(hash)?.scrollIntoView();
      setActiveId(hash);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping = target.matches("input, textarea, select, [contenteditable='true']");
      if (event.key === "/" && !isTyping) {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Seção de outro produto: renderiza o produto antes de rolar, senão o alvo ainda não existe no DOM.
  const navigateTo = (id: string) => {
    const target = productOfSection(id) ?? productId;
    const switching = target !== productId;
    if (switching) flushSync(() => setProductId(target));
    document.getElementById(id)?.scrollIntoView({ behavior: switching ? "auto" : "smooth", block: "start" });
    setActiveId(id);
    setQuery("");
    setMobileNavigationOpen(false);
    window.history.replaceState(null, "", `#${id}`);
  };

  // No menu mobile o drawer fica aberto para escolher a seção do produto novo.
  const switchProduct = (id: ProductId) => {
    if (id === productId) return;
    const home = productById.get(id)?.home ?? "inicio";
    flushSync(() => setProductId(id));
    window.scrollTo({ top: 0 });
    setActiveId(home);
    setQuery("");
    window.history.replaceState(null, "", `#${home}`);
  };

  const openSearchResult = (result: SearchResult) => {
    if (result.modeId) {
      setSelectedModeId(result.modeId);
      navigateTo("criar-sala");
      window.history.replaceState(null, "", `#mode-${result.modeId}`);
      return;
    }
    navigateTo(result.id);
  };

  const navigateModeTabs = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const lastIndex = roomModes.length - 1;
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? lastIndex
        : event.key === "ArrowRight"
          ? (index + 1) % roomModes.length
          : (index - 1 + roomModes.length) % roomModes.length;
    setSelectedModeId(roomModes[nextIndex].id);
    window.history.replaceState(null, "", `#mode-${roomModes[nextIndex].id}`);
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("[role='tab']");
    tabs?.[nextIndex]?.focus();
  };

  const sidebar = (
    <nav className="docs-navigation" aria-label="Documentação da API">
      {product.navigation.map((group) => (
        <div className="nav-group" key={group.label}>
          <p className="nav-group-title">{group.label}</p>
          {group.items.map((item) => (
            <button
              type="button"
              className={`nav-item ${activeId === item.id ? "is-active" : ""}`}
              key={item.id}
              onClick={() => navigateTo(item.id)}
            >
              <span>{item.label}</span>
              {item.method && <MethodBadge method={item.method} />}
            </button>
          ))}
        </div>
      ))}
      <div className="nav-support">
        <div className="support-icon"><BookOpen size={17} aria-hidden="true" /></div>
        <p>Não encontrou o que precisa?</p>
        <a href="mailto:suporte@rushxclub.com">Falar com o suporte <ArrowRight size={14} aria-hidden="true" /></a>
      </div>
    </nav>
  );

  return (
    <div className="app-shell">
      <a className="skip-link" href="#conteudo">Ir para o conteúdo</a>

      <header className="topbar">
        <div className="topbar-row">
          <div className="topbar-brand">
            <div className="brand" aria-label="RX Docs">
              <div className="brand-mark">RX</div>
              <span className="brand-product">Docs</span>
            </div>
          </div>

          <div className="search-wrap">
            <Search size={17} aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar na documentação"
              aria-label="Buscar na documentação"
            />
            <span className="search-shortcut"><Command size={12} aria-hidden="true" /> K</span>
            {query && (
              <div className="search-results" role="listbox" aria-label="Resultados da busca">
                {searchResults.length ? searchResults.map((result, index) => (
                  <button key={`${result.group}-${result.id}-${result.modeId ?? result.label}-${index}`} type="button" onClick={() => openSearchResult(result)} role="option">
                    <span><small>{productById.get(productOfSection(result.id) ?? "rxsalas")?.name} · {result.group}</small>{result.label}</span>
                    {result.method ? <MethodBadge method={result.method} /> : <ArrowRight size={14} aria-hidden="true" />}
                  </button>
                )) : (
                  <div className="search-empty">
                    <Search size={18} aria-hidden="true" />
                    <span>Nenhum tópico encontrado. Tente “sala”, “uid” ou “limites”.</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="topbar-actions">
            <button
              type="button"
              className="mobile-menu-button"
              onClick={() => setMobileNavigationOpen(true)}
              aria-label="Abrir navegação"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            <a className="status-link" href="https://status.rushxclub.com" target="_blank" rel="noreferrer">
              <span className="status-dot" aria-hidden="true" />
              Status da API
            </a>
          </div>
        </div>
        <ProductTabs active={productId} onSelect={switchProduct} />
      </header>

      <div className="layout">
        <aside className="sidebar">{sidebar}</aside>

        {mobileNavigationOpen && (
          <div className="mobile-navigation" role="dialog" aria-modal="true" aria-label="Navegação da documentação">
            <button className="mobile-backdrop" type="button" onClick={() => setMobileNavigationOpen(false)} aria-label="Fechar navegação" />
            <div className="mobile-drawer">
              <div className="mobile-drawer-header">
                <span>Navegação</span>
                <button type="button" onClick={() => setMobileNavigationOpen(false)} aria-label="Fechar navegação">
                  <X size={20} aria-hidden="true" />
                </button>
              </div>
              <ProductSwitcher className="product-switcher--drawer" active={productId} onSelect={switchProduct} />
              {sidebar}
            </div>
          </div>
        )}

        <main id="conteudo" className="content">
          {productId === "rxsalas" ? (
            <>
              <section id="inicio" className="hero-section doc-section">
                <div className="release-note"><span>Nova</span> API no domínio rushxclub.com</div>
                <h1>Crie a sala. Controle o ciclo. Receba o resultado.</h1>
                <p className="hero-copy">
                  A API RX.SALAS entrega salas de Free Fire para bots, plataformas e operações de campeonato. Esta referência acompanha a integração do primeiro request ao placar final.
                </p>
                <div className="hero-actions">
                  <button type="button" className="primary-action" onClick={() => navigateTo("criar-sala")}>
                    Fazer primeira chamada <ArrowRight size={16} aria-hidden="true" />
                  </button>
                  <button type="button" className="secondary-action" onClick={() => navigateTo("webhooks")}>
                    Entender webhooks
                  </button>
                </div>

                <div className="base-url" aria-label="URL base da API">
                  <span className="protocol">HTTPS</span>
                  <code>https://api.rushxclub.com</code>
                  <CopyButton value="https://api.rushxclub.com" label="Copiar URL" />
                </div>

                <div className="request-trace" aria-labelledby="trace-title">
                  <div className="trace-intro">
                    <span className="trace-kicker">Ciclo de uma sala</span>
                    <h2 id="trace-title">Um request, quatro estados operacionais</h2>
                    <p>Acompanhe a mesma sessão. Não crie outra sala quando receber `202`.</p>
                  </div>
                  <ol className="trace-list">
                    <li className="trace-item is-complete">
                      <span className="trace-node"><Check size={14} aria-hidden="true" /></span>
                      <div><strong>Solicitar</strong><span>POST /rooms</span></div><time>0 ms</time>
                    </li>
                    <li className="trace-item is-current">
                      <span className="trace-node"><Clock3 size={14} aria-hidden="true" /></span>
                      <div><strong>Provisionar</strong><span>202 · respeite Retry-After</span></div><time>~2 s</time>
                    </li>
                    <li className="trace-item">
                      <span className="trace-node">3</span>
                      <div><strong>Operar</strong><span>status · start · kick</span></div><time>sessão</time>
                    </li>
                    <li className="trace-item">
                      <span className="trace-node">4</span>
                      <div><strong>Encerrar</strong><span>result · release</span></div><time>final</time>
                    </li>
                  </ol>
                </div>
              </section>

              <section id="autenticacao" className="doc-section">
                <div className="section-icon"><KeyRound size={19} aria-hidden="true" /></div>
                <h2>Autenticação</h2>
                <p>Toda requisição usa sua chave no header <code>X-API-Key</code>. A chave identifica a conta e a carteira responsável pela sala.</p>
                <div className="callout callout--warning">
                  <ShieldCheck size={19} aria-hidden="true" />
                  <div><strong>Trate a chave como senha</strong><span>Use apenas no servidor. Não publique em frontend, logs ou repositórios.</span></div>
                </div>
                <div className="inline-code-row">
                  <code>X-API-Key: rxc_live_sua_chave_aqui</code>
                  <CopyButton value="X-API-Key: rxc_live_sua_chave_aqui" />
                </div>
              </section>

              <section id="criar-sala" className="doc-section endpoint-section">
                <EndpointTitle method="POST" path={selectedMode.custom ? "/rooms/custom" : "/rooms"}>Criar uma sala</EndpointTitle>
                <p>Escolha um preset de Clash Squad pelo campo <code>pref</code> ou monte as regras do zero em <code>/rooms/custom</code>. Use uma <code>Idempotency-Key</code> única por operação. Para Battle Royale, consulte <a href="#criar-sala-br">Criar sala BR</a>.</p>

                <div className="mode-explorer">
                  <div className="mode-tabs-viewport">
                    <div className="mode-tabs" role="tablist" aria-label="Modos disponíveis para criação de sala">
                      {roomModes.map((mode, index) => (
                        <button
                          key={mode.id}
                          id={`mode-tab-${mode.id}`}
                          type="button"
                          role="tab"
                          aria-selected={selectedMode.id === mode.id}
                          aria-controls="mode-detail"
                          tabIndex={selectedMode.id === mode.id ? 0 : -1}
                          className={selectedMode.id === mode.id ? "is-active" : ""}
                          onClick={() => {
                            setSelectedModeId(mode.id);
                            window.history.replaceState(null, "", `#mode-${mode.id}`);
                          }}
                          onKeyDown={(event) => navigateModeTabs(event, index)}
                        >
                          {mode.custom && <span className="mode-tab-signal" aria-hidden="true" />}
                          {mode.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div
                    id="mode-detail"
                    className={`mode-detail ${selectedMode.custom ? "mode-detail--custom" : ""}`}
                    role="tabpanel"
                    aria-labelledby={`mode-tab-${selectedMode.id}`}
                  >
                    <div className="mode-detail-intro">
                      <span className="mode-index">{String(roomModes.indexOf(selectedMode) + 1).padStart(2, "0")} / {roomModes.length}</span>
                      <h3>{selectedMode.label}</h3>
                      <p>{selectedMode.description}</p>
                      {selectedMode.pref ? (
                        <div className="mode-pref">
                          <span>pref</span>
                          <code>{selectedMode.pref}</code>
                          <CopyButton value={selectedMode.pref} />
                        </div>
                      ) : (
                        <div className="mode-pref mode-pref--custom">
                          <span>rota</span>
                          <code>POST /rooms/custom</code>
                          <CopyButton value="POST /rooms/custom" />
                        </div>
                      )}
                      {selectedMode.variants && (
                        <div className="mode-variants" aria-label={`Variantes de ${selectedMode.label}`}>
                          <span className="mode-variants-title">Variantes desta configuração</span>
                          {selectedMode.variants.map((variant) => (
                            <div className="mode-variant" key={variant.pref}>
                              <span>{variant.label}</span>
                              <div>
                                <code>{variant.pref}</code>
                                <p>{variant.economy} — {variant.note}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <dl className="mode-facts">
                      <div><dt>Melhor uso</dt><dd>{selectedMode.useCase}</dd></div>
                      <div><dt>Formato</dt><dd>Clash Squad · 4 × 4</dd></div>
                      <div><dt>Rodadas</dt><dd>{selectedMode.rounds}</dd></div>
                      <div><dt>Economia</dt><dd>{selectedMode.economy}</dd></div>
                      <div><dt>Mecânica central</dt><dd>{selectedMode.mechanic}</dd></div>
                      <div><dt>Loja</dt><dd>{selectedMode.shop}</dd></div>
                    </dl>

                    {selectedMode.custom && (
                      <div className="custom-contract">
                        <div className="custom-contract-header">
                          <div>
                            <span>Contrato avançado</span>
                            <strong>Controle explícito, validação estrita</strong>
                          </div>
                          <span className="custom-beta">config obrigatória</span>
                        </div>
                        <div className="custom-fields">
                          <span><code>functions</code> 13 opções booleanas</span>
                          <span><code>attributes</code> HP, EP, speed e jump de 0 a 7</span>
                          <span><code>shop</code> 1–70 itens; preços múltiplos de 50</span>
                          <span><code>gold_per_round</code> um valor para cada rodada</span>
                        </div>
                        <p>
                          Todas as chaves de <code>config</code> são obrigatórias. Opções experimentais podem ser ignoradas ou recusadas pelo jogo; nesse caso, a cobrança é estornada.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="sample-heading">
                  <div>
                    <span>Request do modo selecionado</span>
                    <strong>{selectedMode.custom ? "Configuração completa" : selectedMode.label}</strong>
                  </div>
                  <code>{selectedMode.custom ? "/rooms/custom" : "/rooms"}</code>
                </div>

                <div className="code-panel">
                  <div className="code-toolbar">
                    <div className="language-tabs" role="tablist" aria-label="Linguagem do exemplo">
                      <button type="button" className={language === "curl" ? "is-active" : ""} onClick={() => setLanguage("curl")} role="tab" aria-selected={language === "curl"}>cURL</button>
                      <button type="button" className={language === "node" ? "is-active" : ""} onClick={() => setLanguage("node")} role="tab" aria-selected={language === "node"}>Node.js</button>
                    </div>
                    <CopyButton value={selectedCodeSamples[language]} />
                  </div>
                  <pre><code>{selectedCodeSamples[language]}</code></pre>
                </div>

                <h3>{selectedMode.custom ? "Campos de config" : "Corpo do request"}</h3>
                <div className="parameter-table" role="table" aria-label="Parâmetros para criar sala">
                  <div className="parameter-row parameter-head" role="row"><span>Campo</span><span>Tipo</span><span>Descrição</span></div>
                  {selectedMode.custom ? (
                    <>
                      <div className="parameter-row" role="row"><code>team_size</code><span>string</span><span>Nesta fase, exatamente <code>4x4</code></span></div>
                      <div className="parameter-row" role="row"><code>map</code><span>string</span><span>Nesta fase, exatamente <code>default</code></span></div>
                      <div className="parameter-row" role="row"><code>rounds</code><span>integer</span><span>De 7 a 13 rodadas</span></div>
                      <div className="parameter-row" role="row"><code>functions</code><span>object</span><span>As 13 chaves booleanas do contrato, sem chaves adicionais</span></div>
                      <div className="parameter-row" role="row"><code>attributes</code><span>object</span><span><code>hp</code>, <code>ep</code>, <code>speed</code> e <code>jump</code>, inteiros de 0 a 7</span></div>
                      <div className="parameter-row" role="row"><code>shop</code><span>array</span><span>1 a 70 itens únicos; preço entre 0 e 12.750, múltiplo de 50</span></div>
                      <div className="parameter-row" role="row"><code>gold_per_round</code><span>array</span><span>Exatamente um inteiro para cada rodada</span></div>
                    </>
                  ) : (
                    <>
                      <div className="parameter-row" role="row"><code>pref</code><span>string</span><span>Modo da sala. Padrão: <code>cs_8_sem_carregamento</code></span></div>
                      <div className="parameter-row" role="row"><code>room_name</code><span>string</span><span>Nome exibido dentro do jogo</span></div>
                      <div className="parameter-row" role="row"><code>password</code><span>string</span><span>Senha entregue aos jogadores</span></div>
                      <div className="parameter-row" role="row"><code>goConsensus</code><span>boolean</span><span>Permite iniciar por consenso com o comando `.go`</span></div>
                    </>
                  )}
                </div>

                <h3>Resposta com sala pronta</h3>
                <div className="response-heading"><span className="response-status">201</span><span>Created</span></div>
                <div className="code-panel code-panel--response">
                  <div className="code-toolbar"><span>application/json</span><CopyButton value={selectedMode.custom ? customResponseSample : responseSample} /></div>
                  <pre><code>{selectedMode.custom ? customResponseSample : responseSample}</code></pre>
                </div>
              </section>

              <section id="criar-sala-br" className="doc-section endpoint-section">
                <EndpointTitle method="POST" path="/rooms">Criar uma sala Battle Royale</EndpointTitle>
                <p>BR usa a mesma base, a mesma chave e o mesmo envelope de criação de Clash Squad. Envie sempre <code>pref: "br_48_padrao"</code>: omitir o preset usa o padrão histórico de CS. Escolha <code>solo</code>, <code>duo</code> ou <code>squad</code> em <code>team_mode</code>; o padrão é <code>squad</code> e a sala tem 48 vagas de jogador.</p>
                <div className="callout callout--warning">
                  <ShieldCheck size={19} aria-hidden="true" />
                  <div>
                    <strong>Disponibilidade dos novos campos · 13/10/2026</strong>
                    <span><code>rules.allow_emulators</code> e <code>room.emulator_check_enabled</code> estão disponíveis nas <code>engine-v2-1</code> e <code>engine-v2-2</code>. Essas engines também retomam o monitoramento BR por checkpoint completo, sem duplicar quedas ou totais e sem novos campos obrigatórios para parceiros. A retomada não migra o host da sala, não recupera dados nunca capturados e não elimina os limites de associação de partidas descritos no guia completo.</span>
                  </div>
                </div>

                <h3>Headers da criação</h3>
                <p>Use uma <code>Idempotency-Key</code> única por operação e preserve a chave ao repetir a mesma tentativa após um timeout. Uma nova tentativa após falha exige uma nova chave; o replay também pode devolver o pedido anterior com status <code>failed</code>.</p>
                <StaticCodeBlock label="HTTP · headers" value={`X-API-Key: API_KEY
Idempotency-Key: exemplo-br-001
Content-Type: application/json`} />

                <h3>Queda avulsa · emuladores bloqueados por padrão</h3>
                <p>O exemplo explicita <code>rules.allow_emulators: false</code>. Omitir essa regra mantém o mesmo bloqueio na engine atualizada. Sem <code>maps</code>, a sala é avulsa; <code>start_delay_seconds: 0</code> desliga o auto-start, então inicie a partida com <code>POST /rooms/:sessionId/start</code>.</p>
                <StaticCodeBlock value={brDefaultRequestSample} />

                <h3>Circuito · permissão explícita de emuladores</h3>
                <p><code>rules.allow_emulators: true</code> libera emuladores. O exemplo pede três quedas na mesma sala. <code>maps</code> aceita de 1 a 5 itens: até um único mapa já cria um circuito. O início da primeira queda segue manual com <code>start_delay_seconds: 0</code>; o intervalo entre as seguintes é de 180 segundos.</p>
                <StaticCodeBlock value={brEmulatorsRequestSample} />

                <div className="reference-disclosures">
                  <details open>
                    <summary>Campos da criação BR <span>{brCreateFields.length}</span></summary>
                    <FieldReference fields={brCreateFields} />
                  </details>
                  <details>
                    <summary>Regras BR <span>{brRuleFields.length}</span></summary>
                    <FieldReference fields={brRuleFields} />
                  </details>
                </div>

                <h3>Permissão de emuladores e checagem do jogo</h3>
                <p><code>rules.allow_emulators</code> é um booleano estrito: <code>"true"</code>, <code>"false"</code>, <code>1</code>, <code>0</code> e <code>null</code> não são aceitos como valores da regra. Valores inválidos devolvem <code>400 valor_nao_booleano</code>; chaves desconhecidas devolvem <code>400 regra_desconhecida</code>.</p>
                <div className="compact-table">
                  <div><code>false ou omitido</code><span>Bloqueia emuladores</span><p>Checagem nativa <code>true</code>; no jogo, <strong>EMULADOR = SIM</strong>.</p></div>
                  <div><code>true</code><span>Permite emuladores</span><p>Checagem nativa <code>false</code>; no jogo, <strong>EMULADOR = DESABILITADO</strong>.</p></div>
                </div>
                <p>Envie a chave somente dentro de <code>rules</code>. Não há alias na raiz do corpo, e ela não faz parte do contrato de CS em <code>/rooms/custom</code>. A política se fixa na criação, vale durante todo o circuito e não pode ser alterada por <code>/edit</code>.</p>
                <p>Confira o valor observado em <code>room.emulator_check_enabled</code> no <a href="#status-sala">status da sala</a>. Ele descreve a checagem nativa; <code>emulator_players</code> e <code>emulator_count</code> descrevem pessoas já observadas e não confirmam a política de entrada.</p>

                <h3>Resposta com sala pronta</h3>
                <div className="response-heading"><span className="response-status">201</span><span>Created · exemplo ilustrativo</span></div>
                <StaticCodeBlock value={brReadyResponseSample} />
                <p>Leia <code>request.room.session_id</code>, <code>request.room.room_id</code> e <code>request.room.password</code>; a sala não está na raiz da resposta. Um <code>202</code> ainda não traz sala pronta: consulte <code>GET /rooms/requests/:requestId</code> e respeite <code>Retry-After</code>. O create de circuito usa o mesmo envelope; o andamento fica em <code>/result</code>.</p>
                <div className="hero-actions">
                  <a className="secondary-action" href="/API_RXSALAS_MODO_BR.md">Guia completo de BR <ExternalLink size={16} aria-hidden="true" /></a>
                </div>
              </section>

              <section id="acompanhar" className="doc-section endpoint-section">
                <EndpointTitle method="GET" path="/rooms/requests/:requestId">Acompanhar uma criação</EndpointTitle>
                <p>A criação é assíncrona. O status HTTP informa se a sala está pronta, em processamento ou se a mesma operação foi reproduzida por idempotência.</p>
                <div className="status-matrix">
                  <div><span className="http-status http-status--ok">201</span><strong>Sala pronta</strong><p>Leia <code>request.room</code> e guarde o <code>session_id</code>.</p></div>
                  <div><span className="http-status http-status--wait">202</span><strong>Processando</strong><p>Consulte o mesmo <code>requestId</code>; não refaça o POST.</p></div>
                  <div><span className="http-status">200</span><strong>Replay seguro</strong><p>A mesma <code>Idempotency-Key</code> devolveu a operação existente.</p></div>
                  <div><span className="http-status http-status--error">400</span><strong>Criação recusada</strong><p>Leia <code>error</code>, <code>reason</code> e, no custom, <code>details</code>.</p></div>
                </div>
                <div className="callout callout--info">
                  <Gauge size={19} aria-hidden="true" />
                  <div><strong>Um 202 pode estar em fila</strong><span>Quando <code>queued.reason</code> for <code>concurrency_limit</code> ou <code>fair_share</code>, respeite o <code>Retry-After</code>. Reenviar não fura a fila.</span></div>
                </div>
                <StaticCodeBlock value={`{
      "requestId": "req_01J7M8FK2X",
      "status": "queued",
      "created": true,
      "queued": {
        "reason": "concurrency_limit",
        "retryAfterSec": 12
      }
    }`} />
              </section>

              <section id="status-sala" className="doc-section endpoint-section">
                <EndpointTitle method="GET" path="/rooms/:sessionId/status">Status da sala</EndpointTitle>
                <p>Retorna uma fotografia do lobby, com times, slots, espectadores, emuladores, perfis opcionais e o grau de confiança do estado.</p>
                <div className="callout callout--warning breaking-change">
                  <ShieldCheck size={19} aria-hidden="true" />
                  <div>
                    <strong>Formato aninhado: releia sua integração</strong>
                    <span><code>room.players</code> agora é um número. A lista está em <code>teams[].slots[]</code>; usar <code>players.length</code> passa a devolver <code>undefined</code>.</span>
                  </div>
                </div>
                <h3>Parâmetros de consulta</h3>
                <div className="compact-table">
                  <div><code>fresh=1</code><span>Padrão</span><p>Consulta o servidor do jogo agora.</p></div>
                  <div><code>fresh=0</code><span>Cache</span><p>Resposta barata; pode estar desatualizada.</p></div>
                  <div><code>profiles=1</code><span>Opcional</span><p>Inclui <code>skills</code> e <code>pet</code> por jogador.</p></div>
                </div>
                <StaticCodeBlock value={statusResponseSample} />
                <p>O exemplo de CS usa <code>room.emulator_check_enabled: null</code> porque a checagem ainda é desconhecida; não afirma que CS recebeu a atualização de BR.</p>
                <h3>BR · checagem de emuladores observada</h3>
                <p>Na engine atualizada, <code>room.emulator_check_enabled</code> é <code>boolean | null</code>: <code>true</code> confirma checagem ligada, <code>false</code> confirma desligada e <code>null</code> significa ainda não observada. O campo não é a permissão de entrada nem o eco do request. Ausência do campo, inclusive quando o servidor omite um valor <code>false</code>, deve ser tratada como desconhecido.</p>
                <StaticCodeBlock label="application/json · trecho ilustrativo de BR" value={brStatusResponseSample} />
                <p>Neste exemplo, a checagem está ligada e nenhum jogador de emulador foi observado. <code>emulator_count: 0</code> sozinho não comprova bloqueio; os campos <code>emulator_players</code> e <code>emulator_count</code> são independentes da configuração da sala.</p>
                <div className="reference-disclosures">
                  <details open>
                    <summary>Campos da raiz <span>{statusRootFields.length}</span></summary>
                    <FieldReference fields={statusRootFields} />
                  </details>
                  <details>
                    <summary>Objeto room <span>{roomFields.length}</span></summary>
                    <FieldReference fields={roomFields} />
                  </details>
                  <details>
                    <summary>Times, slots e espectadores <span>{slotFields.length}</span></summary>
                    <p className="detail-note">Times vazios não aparecem. Para listar todos, use <code>status.teams.flatMap((team) =&gt; team.slots)</code>.</p>
                    <FieldReference fields={slotFields} />
                  </details>
                </div>
                <div className="signal-guide">
                  <div><span className="signal signal--ok" /><strong>Confiável</strong><p><code>status_stale=false</code> e <code>host_online=true</code>.</p></div>
                  <div><span className="signal signal--wait" /><strong>Último estado</strong><p><code>status_stale=true</code>; leia <code>status_error</code>.</p></div>
                  <div><span className="signal signal--error" /><strong>Host offline</strong><p>Se persistir, libere a sala e crie outra.</p></div>
                </div>
              </section>

              <section id="operacoes-sala" className="doc-section">
                <h2>Controlar a sala</h2>
                <p>O <code>session_id</code> conecta as operações e limita o acesso às salas pertencentes à sua chave.</p>
                <div className="endpoint-list">
                  <div><MethodBadge method="POST" /><code>/rooms/:sessionId/start</code><span>Inicia, confirma e devolve o host ao pool</span></div>
                  <div><MethodBadge method="POST" /><code>/rooms/:sessionId/kick</code><span>Remove pelo UID antes do início</span></div>
                  <div><MethodBadge method="POST" /><code>/rooms/:sessionId/edit</code><span>Altera nome, senha ou modo</span></div>
                  <div><MethodBadge method="POST" /><code>/rooms/:sessionId/chat</code><span>Envia uma mensagem ao lobby</span></div>
                  <div><MethodBadge method="POST" /><code>/rooms/:sessionId/release</code><span>Encerra e devolve o recurso ao pool</span></div>
                </div>
                <div className="operation-focus">
                  <div className="operation-focus-copy">
                    <span>Operação crítica</span>
                    <h3>Start é síncrono por definição</h3>
                    <p>A chamada envia a contagem, aguarda a confirmação da Garena e executa o handoff. Use timeout de pelo menos <strong>30 segundos</strong>.</p>
                  </div>
                  <div className="operation-timing" aria-label="Tempo típico do start">
                    <div><span>Countdown</span><strong>~4 s</strong></div>
                    <div><span>Confirmação</span><strong>0,5–5 s</strong></div>
                    <div><span>Handoff</span><strong>~4,3 s</strong></div>
                    <div className="operation-total"><span>Total típico</span><strong>~9,5 s</strong></div>
                  </div>
                </div>
                <StaticCodeBlock value={startResponseSample} />
                <div className="tracking-states">
                  <div><code>true</code><strong>Placar registrado</strong><p>Pode consultar o resultado.</p></div>
                  <div><code>false</code><strong>Sem rastreamento</strong><p>Não haverá placar; leia <code>reason</code>.</p></div>
                  <div><code>null</code><strong>Ainda processando</strong><p>Continue o polling. Nunca trate como <code>false</code>.</p></div>
                </div>
                <div className="callout callout--warning">
                  <ShieldCheck size={19} aria-hidden="true" />
                  <div><strong>409 nunca significa sucesso</strong><span><code>start_not_confirmed</code> quer dizer que a partida não começou. Repetir o mesmo POST é seguro.</span></div>
                </div>
                <details className="operation-recipes">
                  <summary>Payloads de kick e edit</summary>
                  <div className="recipe-grid">
                    <StaticCodeBlock label="POST /kick" value={`{ "uid": "123456789" }`} />
                    <StaticCodeBlock label="POST /edit" value={`{
      "room_name": "NOVO NOME",
      "password": "456",
      "pref": "cs_8_com_carregamento"
    }`} />
                  </div>
                </details>
              </section>

              <section id="resultados" className="doc-section endpoint-section">
                <EndpointTitle method="GET" path="/rooms/:sessionId/result">Resultado da partida</EndpointTitle>
                <p>O placar é calculado pela diferença entre as estatísticas anteriores e posteriores. O rastreamento começa no <code>start</code>.</p>
                <div className="result-states">
                  <div><span>jogando</span><p>Resultado ainda não consolidado.</p><code>Retry-After: 10</code></div>
                  <div><span>finalizada</span><p>Placar definitivo; não muda mais.</p><code>poll_after_seconds: null</code></div>
                  <div><span>no_match</span><p>Nenhuma partida detectada em 25 minutos.</p><code>poll_after_seconds: null</code></div>
                </div>
                <StaticCodeBlock value={resultResponseSample} />
                <div className="callout callout--info">
                  <Gauge size={19} aria-hidden="true" />
                  <div><strong>Um MVP por time</strong><span><code>gold</code> identifica o MVP do vencedor e <code>silver</code> o do derrotado. Com <code>played=false</code>, as estatísticas são <code>null</code>.</span></div>
                </div>
              </section>

              <section id="join-events" className="doc-section endpoint-section">
                <EndpointTitle method="GET" path="/rooms/:sessionId/join-events?since=:cursor">Entradas no lobby</EndpointTitle>
                <p>Polling incremental para atualizar painéis ao vivo sem baixar o estado completo. Cada UID aparece uma vez.</p>
                <StaticCodeBlock value={`{
      "events": [{
        "seq": 1,
        "uid": "10904386688",
        "name": "FELPSZ 2M",
        "ts": 1785766677849
      }],
      "cursor": 1
    }`} />
                <div className="callout callout--info">
                  <Gauge size={19} aria-hidden="true" />
                  <div><strong>Avance sempre o cursor</strong><span>Envie o último <code>cursor</code> em <code>since</code>. O buffer guarda até 100 eventos e existe apenas enquanto a sala estiver ativa.</span></div>
                </div>
              </section>

              <section id="webhooks" className="doc-section">
                <div className="section-icon"><Webhook size={19} aria-hidden="true" /></div>
                <h2>Webhooks da partida</h2>
                <p>O callback pertence à sala e expira com ela. Ele reduz a latência, mas não substitui totalmente o polling de segurança.</p>
                <StaticCodeBlock value={`{
      "pref": "cs_8_sem_carregamento",
      "room_name": "SALA X",
      "callback": {
        "url": "https://seu-sistema.com/rx-salas/webhook",
        "secret": "um-segredo-longo-e-aleatorio",
        "events": ["room.updated", "room.started", "room.result"]
      }
    }`} />
                <div className="event-grid">
                  <div><span className="event-dot" /><code>room.updated</code><p>Jogador entrou, saiu ou mudou de time.</p></div>
                  <div><span className="event-dot" /><code>room.started</code><p>A Garena confirmou o início.</p></div>
                  <div><span className="event-dot event-dot--coral" /><code>room.result</code><p>O placar final foi consolidado.</p></div>
                </div>
                <h3>Verifique a assinatura</h3>
                <p className="subsection-copy">Calcule HMAC-SHA256 sobre <code>timestamp.corpo_cru</code>. Rejeite timestamps com mais de cinco minutos e compare os hashes em tempo constante.</p>
                <StaticCodeBlock value={webhookVerificationSample} label="Node.js · verificação" />
                <div className="delivery-rules">
                  <div><strong>Responda primeiro</strong><p>Retorne <code>2xx</code> após validar e processe depois.</p></div>
                  <div><strong>Faça deduplicação</strong><p>Guarde <code>X-RXSalas-Delivery</code>.</p></div>
                  <div><strong>Não assuma ordem global</strong><p>Use <code>sent_at</code> para ignorar um retrato antigo.</p></div>
                  <div><strong>Mantenha a recuperação</strong><p>Faça polling lento de 30–60 s até <code>room.result</code>.</p></div>
                </div>
                <p className="retry-line"><strong>Retentativas:</strong> 1 s, 5 s, 30 s, 2 min e 10 min; a sexta falha encerra a entrega.</p>
              </section>

              <section id="cobranca" className="doc-section">
                <h2>Cobrança e saldo</h2>
                <p>O <code>billing_mode</code> pertence à conta parceira e não é enviado na criação. Ele define como cada sala entregue afeta a carteira.</p>
                <div className="billing-table">
                  {billingModes.map((mode) => (
                    <div key={mode.name}>
                      <code>{mode.name}</code>
                      <p>{mode.behavior}</p>
                      <span>{mode.gate}</span>
                    </div>
                  ))}
                </div>
                <div className="callout callout--info">
                  <ShieldCheck size={19} aria-hidden="true" />
                  <div><strong>Plano ativo tem prioridade</strong><span>Uma assinatura ilimitada vigente dispensa a cobrança mesmo em <code>prepaid</code>, <code>postpaid</code> ou <code>discount</code>.</span></div>
                </div>
              </section>

              <section id="custom-ref" className="doc-section">
                <h2>Referência da sala personalizada</h2>
                <p><code>POST /rooms/custom</code> exige uma configuração completa. Chaves desconhecidas são recusadas para impedir que uma opção digitada incorretamente seja ignorada.</p>
                <h3>Funções</h3>
                <div className="function-reference">
                  {customFunctions.map((item) => (
                    <div key={item.name}>
                      <code>{item.name}</code>
                      <p>{item.description}</p>
                      <span className={item.status === "experimental" ? "is-experimental" : ""}>{item.status}</span>
                    </div>
                  ))}
                </div>
                <div className="callout callout--warning">
                  <ShieldCheck size={19} aria-hidden="true" />
                  <div><strong>Experimental significa não validado em sala real</strong><span>O jogo pode ignorar ou recusar a opção. Uma recusa segue o fluxo normal de falha e estorno.</span></div>
                </div>
                <h3>Atributos, loja e ouro</h3>
                <div className="compact-table">
                  <div><code>attributes.*</code><span>0–7</span><p><code>hp</code>, <code>ep</code>, <code>speed</code> e <code>jump</code>. Use 1 como padrão.</p></div>
                  <div><code>shop[].price</code><span>0–12.750</span><p>Inteiro múltiplo de 50; o preço vale em todos os rounds.</p></div>
                  <div><code>gold_per_round</code><span>int32[]</span><p>Um valor por rodada, na mesma quantidade indicada em <code>rounds</code>.</p></div>
                </div>
                <details className="catalog-disclosure">
                  <summary>Catálogo de itens aceitos <span>{itemCatalog.reduce((total, group) => total + group.items.length, 0)}</span></summary>
                  <div className="item-catalog">
                    {itemCatalog.map((group) => (
                      <div key={group.group}>
                        <strong>{group.group}</strong>
                        <div>{group.items.map((item) => <code key={item}>{item}</code>)}</div>
                      </div>
                    ))}
                  </div>
                </details>
              </section>

              <section id="erros" className="doc-section">
                <h2>Erros e recuperação</h2>
                <p>Use o campo <code>error</code> para decidir a recuperação. O status HTTP sozinho não diferencia todos os casos.</p>
                <div className="error-list">
                  {apiErrors.map((error) => (
                    <div key={`${error.status}-${error.code}`}><span>{error.status}</span><code>{error.code}</code><p>{error.action}</p></div>
                  ))}
                </div>
                <div className="error-notes">
                  <p><strong>Saldo:</strong> trate <code>400 insufficient_wallet_balance</code> e <code>402 insufficient_partner_balance</code> como a mesma ação.</p>
                  <p><strong>503:</strong> sem <code>error</code> significa falta de capacidade e não cobra; <code>room_host_offline</code> ocorre numa sala já criada.</p>
                </div>
              </section>

              <section id="limites" className="doc-section">
                <div className="section-icon"><Gauge size={19} aria-hidden="true" /></div>
                <h2>Limites e cotas</h2>
                <p>As respostas de criação informam o estado das janelas por minuto e por dia. Leia os headers para ajustar o ritmo antes de receber <code>429</code>.</p>
                <div className="headers-list">
                  <div><code>X-RateLimit-Limit-Minute</code><span>Teto da janela atual</span></div>
                  <div><code>X-RateLimit-Remaining-Minute</code><span>Criações ainda disponíveis</span></div>
                  <div><code>X-RateLimit-Reset-Minute</code><span>Epoch do próximo reset</span></div>
                  <div><code>X-RateLimit-Limit-Day</code><span>Teto da janela móvel de 24 horas</span></div>
                  <div><code>X-RateLimit-Remaining-Day</code><span>Criações restantes no dia</span></div>
                  <div><code>X-RateLimit-Reset-Day</code><span>Epoch do reset diário</span></div>
                  <div><code>Retry-After</code><span>Segundos antes da próxima tentativa</span></div>
                </div>
                <div className="scope-list">
                  <code>request_per_minute</code><code>room_per_minute</code><code>room_per_day</code>
                </div>
                <p className="subsection-copy">Os pares de minuto e dia sempre aparecem. Sem cota comercial, o valor representa o teto de segurança da plataforma.</p>
              </section>

              <section id="boas-praticas" className="doc-section">
                <h2>Checklist para produção</h2>
                <p>Estas regras evitam duplicidade, perda de placar, retenção desnecessária de contas e mensagens incorretas para o jogador.</p>
                <ol className="production-checklist">
                  <li><span>01</span><div><strong>Crie quando os jogadores estiverem prontos</strong><p>Uma sala parada segura uma conta do pool e consome sua concorrência.</p></div></li>
                  <li><span>02</span><div><strong>Use uma Idempotency-Key por operação</strong><p>Repita a mesma chave apenas para recuperar uma tentativa de rede.</p></div></li>
                  <li><span>03</span><div><strong>Respeite Retry-After</strong><p>Vale para criação em fila, rate limit e polling do resultado.</p></div></li>
                  <li><span>04</span><div><strong>Dê 30 segundos ao start</strong><p>Timeout curto pode reportar falha para uma partida que iniciou.</p></div></li>
                  <li><span>05</span><div><strong>Mantenha webhook e polling lento</strong><p>Webhook entrega velocidade; polling oferece recuperação.</p></div></li>
                  <li><span>06</span><div><strong>Sempre chame release</strong><p>Faça isso mesmo quando a sala for abandonada antes da partida.</p></div></li>
                  <li><span>07</span><div><strong>Nunca exponha a API key</strong><p>Ela identifica a conta e pode debitar saldo real.</p></div></li>
                </ol>
              </section>
            </>
          ) : (
            <>
              <section id="consulta-jogador" className="hero-section doc-section">
                <div className="release-note"><span>Nova</span> API de consulta em search.rushxclub.com</div>
                <h1>Informe o UID. Receba o perfil do jogador.</h1>
                <p className="hero-copy">
                  A API RX.SEARCH lê o perfil público de um jogador do Free Fire pelo UID. Ela roda separada da criação de salas: outro host, outra chave e cota própria.
                </p>
                <div className="hero-actions">
                  <button type="button" className="primary-action" onClick={() => navigateTo("consulta-v1")}>
                    Fazer primeira consulta <ArrowRight size={16} aria-hidden="true" />
                  </button>
                  <button type="button" className="secondary-action" onClick={() => navigateTo("consulta-erros")}>
                    Ver erros e limites
                  </button>
                </div>

                <div className="base-url" aria-label="URL base da API de consulta">
                  <span className="protocol">HTTPS</span>
                  <code>https://search.rushxclub.com</code>
                  <CopyButton value="https://search.rushxclub.com" label="Copiar URL" />
                </div>

                <div className="endpoint-list">
                  <div><MethodBadge method="GET" /><code>/v1/freefire/player/:uid</code><span>Identificação enxuta e blacklist</span></div>
                  <div><MethodBadge method="GET" /><code>/v2/freefire/player/:uid</code><span>Perfil completo com passes</span></div>
                  <div><MethodBadge method="GET" /><code>/freefire/player/:uid</code><span>Rota anterior às versões; responde como a v2</span></div>
                </div>
                <div className="callout callout--info">
                  <Gauge size={19} aria-hidden="true" />
                  <div><strong>Prefira a v1 quando bastar</strong><span>A v1 responde cerca de 100 bytes e consulta a Garena sem a galeria de passes. Use a v2 só quando precisar do perfil inteiro.</span></div>
                </div>
                <p className="subsection-copy">A rota sem versão continua funcionando e responde exatamente como a v2, com os headers <code>Deprecation: true</code> e <code>Link</code> apontando para a v2. Integrações novas devem usar <code>/v1</code> ou <code>/v2</code>.</p>
              </section>

              <section id="consulta-autenticacao" className="doc-section">
                <div className="section-icon"><KeyRound size={19} aria-hidden="true" /></div>
                <h2>Autenticação</h2>
                <p>A chave da consulta começa com <code>rxs_</code> e não vale na API de salas, nem a chave <code>rxc_live_</code> vale aqui. Envie em <code>x-api-key</code> ou <code>Authorization: Bearer</code>.</p>
                <div className="callout callout--warning">
                  <ShieldCheck size={19} aria-hidden="true" />
                  <div><strong>Chave só no header</strong><span>Na query string ela é recusada, porque vazaria em logs de acesso. Use apenas no servidor.</span></div>
                </div>
                <div className="inline-code-row">
                  <code>x-api-key: rxs_sua_chave_aqui</code>
                  <CopyButton value="x-api-key: rxs_sua_chave_aqui" />
                </div>
              </section>

              <section id="consulta-v1" className="doc-section endpoint-section">
                <EndpointTitle method="GET" path="/v1/freefire/player/:uid">Jogador · v1</EndpointTitle>
                <p>Resposta mínima para identificar o jogador e saber se a conta está banida pela Garena.</p>
                <StaticCodeBlock label="curl" value={`curl "https://search.rushxclub.com/v1/freefire/player/123456789" \\
      -H "x-api-key: $RXSALAS_SEARCH_KEY"`} />
                <StaticCodeBlock value={playerV1Sample} />
                <div className="reference-disclosures">
                  <details open>
                    <summary>Campos da v1 <span>{playerV1Fields.length}</span></summary>
                    <FieldReference fields={playerV1Fields} />
                  </details>
                </div>
                <div className="tracking-states">
                  <div><code>true</code><strong>Banida</strong><p>O anti-hack da Garena marca a conta como banida.</p></div>
                  <div><code>false</code><strong>Limpa</strong><p>O anti-hack respondeu e não há ban.</p></div>
                  <div><code>null</code><strong>Sem resposta</strong><p>O anti-hack não respondeu agora. Nunca trate como <code>false</code>.</p></div>
                </div>
              </section>

              <section id="consulta-v2" className="doc-section endpoint-section">
                <EndpointTitle method="GET" path="/v2/freefire/player/:uid">Jogador · v2</EndpointTitle>
                <p>Perfil público completo: ranks, visual, habilidades, guilda, pet, honra e passes, além da blacklist e do período informado pela Garena.</p>
                <StaticCodeBlock label="curl" value={`curl "https://search.rushxclub.com/v2/freefire/player/123456789" \\
      -H "Authorization: Bearer $RXSALAS_SEARCH_KEY"`} />
                <StaticCodeBlock label="application/json · trecho" value={playerV2Sample} />
                <div className="reference-disclosures">
                  <details open>
                    <summary>Campos da v2 <span>{playerV2Fields.length}</span></summary>
                    <FieldReference fields={playerV2Fields} />
                  </details>
                </div>
                <div className="callout callout--info">
                  <Gauge size={19} aria-hidden="true" />
                  <div><strong>Campos novos podem aparecer</strong><span>A v2 acompanha o perfil do jogo. Ignore campos desconhecidos em vez de rejeitar a resposta.</span></div>
                </div>
              </section>

              <section id="consulta-erros" className="doc-section">
                <h2>Erros e limites da consulta</h2>
                <p>Todo erro responde <code>{"{ \"ok\": false, \"error\": \"código\" }"}</code>. Decida a recuperação pelo <code>error</code>.</p>
                <div className="error-list">
                  {playerErrors.map((error) => (
                    <div key={`${error.status}-${error.code}`}><span>{error.status}</span><code>{error.code}</code><p>{error.action}</p></div>
                  ))}
                </div>
                <div className="headers-list">
                  <div><code>X-RateLimit-Limit</code><span>Consultas por minuto da sua chave</span></div>
                  <div><code>X-RateLimit-Remaining</code><span>Consultas restantes no minuto atual</span></div>
                  <div><code>Retry-After</code><span>Segundos antes da próxima tentativa</span></div>
                </div>
                <p className="subsection-copy">O perfil fica em cache por 5 minutos e consultas repetidas do mesmo UID também contam na cota. Um UID inválido responde <code>400</code> sem consumir cota.</p>
              </section>
            </>
          )}

          <footer className="content-footer">
            <div><strong>{product.name} Docs</strong><span>{product.footerNote}</span></div>
            <a href="mailto:suporte@rushxclub.com">Reportar inconsistência <ExternalLink size={14} aria-hidden="true" /></a>
          </footer>
        </main>

        <aside className="toc" aria-label="Nesta página">
          <p>Nesta página</p>
          {product.toc.map((item) => (
            <button type="button" key={item.id} onClick={() => navigateTo(item.id)}>{item.label}</button>
          ))}
          <div className="toc-runtime">
            <Terminal size={16} aria-hidden="true" />
            <span><small>Base URL</small>{product.baseUrl}</span>
          </div>
          <div className="toc-security">
            <CircleCheck size={15} aria-hidden="true" /> TLS 1.2+
          </div>
        </aside>
      </div>
    </div>
  );
}
